import { motion, AnimatePresence } from 'framer-motion';
import { X, Trophy, TrendingUp, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ConfettiEffect from './ConfettiEffect';
import { getEarnedSavingsBadges, Badge } from './BadgeSystem';

export default function MilestoneCelebration({ isOpen, milestone, onClose }) {
  if (!isOpen || !milestone) return null;

  // Get the badge earned at this milestone
  const earnedBadges = getEarnedSavingsBadges(milestone.amount);
  const newBadge = earnedBadges[earnedBadges.length - 1];

  const getMilestoneIcon = (amount) => {
    if (amount >= 5000) return <Trophy className="w-16 h-16 text-amber-500" />;
    if (amount >= 1000) return <Star className="w-16 h-16 text-purple-500" />;
    return <TrendingUp className="w-16 h-16 text-green-500" />;
  };

  const getConfettiType = (amount) => {
    if (amount >= 5000) return 'fireworks';
    if (amount >= 1000) return 'cannon';
    if (amount >= 500) return 'sides';
    return 'burst';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <ConfettiEffect type={getConfettiType(milestone.amount)} />
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 100 }}
            animate={{ 
              opacity: 1, 
              scale: 1, 
              y: 0,
              rotate: [0, -5, 5, -5, 0]
            }}
            exit={{ opacity: 0, scale: 0.5, y: 100 }}
            transition={{ 
              type: "spring", 
              duration: 0.7,
              bounce: 0.5
            }}
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md"
          >
            <div className="bg-gradient-to-br from-white via-amber-50 to-indigo-50 rounded-3xl shadow-2xl p-8 mx-4 relative overflow-hidden">
              {/* Animated background elements */}
              <motion.div
                className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full opacity-20"
                animate={{
                  scale: [1, 1.5, 1],
                  rotate: [0, 360]
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              <motion.div
                className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full opacity-20"
                animate={{
                  scale: [1, 1.5, 1],
                  rotate: [360, 0]
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />

              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-stone-400 hover:text-stone-600 transition-colors z-10"
                data-testid="close-milestone-modal"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="relative z-10">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ 
                    delay: 0.2, 
                    type: "spring",
                    stiffness: 200
                  }}
                  className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 rounded-full flex items-center justify-center shadow-2xl"
                >
                  {getMilestoneIcon(milestone.amount)}
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-4xl font-bold text-center text-stone-900 mb-2"
                >
                  🎉 Milestone Reached!
                </motion.h2>

                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-center mb-6"
                >
                  <div className="text-6xl font-bold bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent mb-2">
                    ${milestone.amount.toLocaleString()}
                  </div>
                  <div className="text-xl text-stone-700 font-medium">
                    {milestone.message}
                  </div>
                </motion.div>

                {/* Badge Earned Section */}
                {newBadge && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.45 }}
                    className="bg-white/80 backdrop-blur-sm rounded-xl p-4 mb-4 text-center"
                  >
                    <div className="text-sm text-stone-500 mb-2">Badge Unlocked!</div>
                    <div className="flex items-center justify-center">
                      <Badge badge={newBadge} size="md" showDescription={true} />
                    </div>
                  </motion.div>
                )}

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-white/60 backdrop-blur-sm rounded-xl p-4 mb-6"
                >
                  <p className="text-center text-stone-600">
                    You're building something amazing! Keep saving and watch your wealth grow.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <Button
                    onClick={onClose}
                    data-testid="continue-saving-button"
                    className="w-full rounded-full py-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg text-lg"
                  >
                    Continue Saving 💪
                  </Button>
                </motion.div>

                {milestone.nextMilestone && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                    className="mt-4 text-center text-sm text-stone-500"
                  >
                    Next milestone: ${milestone.nextMilestone.toLocaleString()}
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
